/**
 * OuraConnectButton — Shows Oura connection status and provides
 * connect/disconnect actions for the therapist dashboard.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOuraApi } from "../hooks/useOuraApi";
import { useToast } from "./Toast";

type Props = {
  patientId: string;
};

/** Oura's identity is the ring itself — a simple ring glyph. */
const OuraGlyph: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth={3.4} aria-hidden>
    <circle cx="12" cy="12" r="8.5" />
  </svg>
);

/* Evocative monochrome glyphs for the not-yet-shipped wearable connectors. */
const wipGlyphs: Record<string, React.ReactNode> = {
  apple: (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor" aria-hidden>
      {/* Apple Health: heart */}
      <path d="M12 20.4 5.2 13.6a4.6 4.6 0 0 1 0-6.5 4.6 4.6 0 0 1 6.5 0l.3.3.3-.3a4.6 4.6 0 0 1 6.5 6.5L12 20.4z" />
    </svg>
  ),
  fitbit: (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor" aria-hidden>
      {/* Fitbit: diamond of dots */}
      <circle cx="12" cy="4" r="1.7" /><circle cx="7" cy="9" r="1.7" /><circle cx="17" cy="9" r="1.7" />
      <circle cx="12" cy="12" r="2.2" /><circle cx="7" cy="15" r="1.7" /><circle cx="17" cy="15" r="1.7" />
      <circle cx="12" cy="20" r="1.7" />
    </svg>
  ),
  garmin: (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="currentColor" aria-hidden>
      {/* Garmin: delta */}
      <path d="M12 4 21 20H3L12 4z" />
    </svg>
  ),
  whoop: (
    <svg viewBox="0 0 24 24" width={14} height={14} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {/* Whoop: strain pulse */}
      <path d="M2 12h4l3-7 5 14 3-7h5" />
    </svg>
  ),
};

/** Famous wearables we don't integrate yet — visible as wip so therapists
 * know they're coming. Purely presentational, deliberately unclickable. */
export const WipWearableConnectors: React.FC = () => {
  const { t } = useTranslation();
  const entries: { key: keyof typeof wipGlyphs; name: string }[] = [
    { key: "apple", name: "Apple Health" },
    { key: "fitbit", name: "Fitbit" },
    { key: "garmin", name: "Garmin" },
    { key: "whoop", name: "Whoop" },
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
      {entries.map((e) => (
        <button
          key={e.key}
          type="button"
          disabled
          title={t("patients.connectorWipTooltip")}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "5px 10px",
            border: "1px dashed var(--color-border-strong)",
            borderRadius: 8,
            fontSize: 12,
            background: "var(--color-subsurface)",
            color: "var(--color-muted)",
            cursor: "not-allowed",
            opacity: 0.75,
          }}
        >
          {wipGlyphs[e.key]}
          {e.name}
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "var(--color-accent)",
              border: "1px solid currentColor",
              borderRadius: 4,
              padding: "1px 4px",
            }}
          >
            {t("patients.connectorWipTag")}
          </span>
        </button>
      ))}
    </div>
  );
};

const OuraConnectButton: React.FC<Props> = ({ patientId }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { createInvite, getStatus, disconnect } = useOuraApi();

  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [inviteUrl, setInviteUrl] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Check connection status on mount / patient change
  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    setLoading(true);
    getStatus(patientId)
      .then((s) => {
        if (!cancelled) setConnected(s.connected);
      })
      .catch(() => {
        if (!cancelled) setConnected(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [patientId, getStatus]);

  const handleConnect = useCallback(async () => {
    try {
      const result = await createInvite(patientId);
      setInviteUrl(result.invite_url);
      setShowModal(true);
      setCopied(false);
    } catch (e) {
      toast.error(
        `${t("oura.connectError")}: ${e instanceof Error ? e.message : String(e)}`
      );
    }
  }, [patientId, createInvite, toast, t]);

  const handleDisconnect = useCallback(async () => {
    try {
      await disconnect(patientId);
      setConnected(false);
      toast.info(t("oura.disconnected"));
    } catch (e) {
      toast.error(
        `${t("oura.disconnectError")}: ${e instanceof Error ? e.message : String(e)}`
      );
    }
  }, [patientId, disconnect, toast, t]);

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(inviteUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [inviteUrl]);

  if (!patientId || loading) return null;

  const btnBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    fontSize: 12,
    cursor: "pointer",
    background: "var(--color-surface)",
  };

  if (connected) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: "var(--color-success)",
            display: "inline-block",
          }}
        />
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "var(--color-success)", fontWeight: 600 }}>
          <OuraGlyph />
          {t("oura.connected")}
        </span>
        <button
          style={{ ...btnBase, color: "var(--color-danger)", borderColor: "var(--color-danger-border)" }}
          onClick={handleDisconnect}
          title={t("oura.disconnect")}
        >
          {t("oura.disconnect")}
        </button>
      </div>
    );
  }

  return (
    <>
      <button style={btnBase} onClick={handleConnect}>
        <OuraGlyph />
        {t("oura.connect")}
      </button>

      {showModal && (
        <>
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 3000,
            }}
            onClick={() => setShowModal(false)}
          />
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "var(--color-surface)",
              borderRadius: 12,
              boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
              padding: 24,
              width: "min(480px, 90vw)",
              zIndex: 3001,
            }}
          >
            <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700 }}>
              {t("oura.inviteTitle")}
            </h3>
            <p style={{ fontSize: 13, color: "var(--color-muted)", marginBottom: 12 }}>
              {t("oura.inviteHint")}
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <input
                readOnly
                value={inviteUrl}
                style={{
                  flex: 1,
                  padding: "8px 10px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 6,
                  fontSize: 12,
                  color: "var(--color-ink)",
                }}
                onClick={(e) => (e.target as HTMLInputElement).select()}
              />
              <button
                style={{
                  ...btnBase,
                  background: copied ? "var(--color-success)" : "var(--color-brand)",
                  color: "#fff",
                  borderColor: copied ? "var(--color-success)" : "var(--color-brand)",
                }}
                onClick={handleCopy}
              >
                {copied ? t("oura.copied") : t("oura.copyLink")}
              </button>
            </div>
            <div style={{ textAlign: "right", marginTop: 16 }}>
              <button
                style={{ ...btnBase }}
                onClick={() => setShowModal(false)}
              >
                {t("common.close")}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default OuraConnectButton;
